import * as Blockly from 'blockly/core';
import { defineBlock } from '@/(routers)/boards/editor/block/blockRegistry';
import { suppressShadowSound } from '@/(routers)/boards/editor/block/blockSoundControl';
import { CATEGORIES } from '@/(routers)/boards/editor/block/definitions/categories';
import { FieldStyledBadgeLabel } from '@/(routers)/boards/editor/block/definitions/fields/fieldStyledBadgeLabel';
import { FieldStyledLabel } from '@/(routers)/boards/editor/block/definitions/fields/fieldStyledLabel';

const POSITION_OPTIONS = {
    orange: {
        rectColour: CATEGORIES.display.color,
        options: [
            { value: 'UPPER_LEFT', label: '左上' },
            { value: 'LOWER_LEFT', label: '左下' },
            { value: 'UPPER_RIGHT', label: '右上' },
            { value: 'LOWER_RIGHT', label: '右下' },
        ],
    },
    blue: {
        rectColour: CATEGORIES.display.color,
        options: [
            { value: 'UPPER_LEFT', label: '左上' },
            { value: 'LOWER_LEFT', label: '左下' },
            { value: 'UPPER_RIGHT', label: '右上' },
            { value: 'LOWER_RIGHT', label: '右下' },
        ],
    },
} as const;

const MODE_OPTIONS = {
    rectColour: CATEGORIES.display.color,
    options: [
        { value: 'INT', label: '整数' },
        { value: 'FLOAT', label: '小数' },
    ],
};

function buildShadow(type: string, fields: Record<string, string>): Element {
    const shadowDom = Blockly.utils.xml.createElement('shadow');
    shadowDom.setAttribute('type', type);
    for (const [k, v] of Object.entries(fields)) {
        const fieldEl = Blockly.utils.xml.createElement('field');
        fieldEl.setAttribute('name', k);
        fieldEl.textContent = v;
        shadowDom.appendChild(fieldEl);
    }
    return shadowDom;
}

defineBlock({
    type: 'seven_seg_number',
    colour: CATEGORIES.display.color,
    tooltip: '7セグメントディスプレイに数値を表示します',
    movable: true,
    deletable: true,
    previousStatement: true,
    nextStatement: true,
    inputs: [
        {
            kind: 'dummy',
            fields: [
                { type: 'image', icon: { src: '/images/block/seven_seg.svg', size: 65 } },
                {
                    type: 'field',
                    instance: () => new FieldStyledLabel('7SEG', { fontSize: 15, fontWeight: 'bold' }),
                },
                {
                    type: 'field',
                    instance: () => new FieldStyledBadgeLabel('数値'),
                },
                { type: 'label', text: ' ' },
                {
                    type: 'dropdown',
                    name: 'POSITION',
                    rectColour: POSITION_OPTIONS.orange.rectColour,
                    options: [...POSITION_OPTIONS.orange.options],
                },
                { type: 'label', text: 'に' },
            ],
        },
        {
            kind: 'value',
            name: 'VALUE',
            check: 'Number',
            shadow: { type: 'number_seven_seg', fields: { NUM: '0' } },
        },
        {
            kind: 'dummy',
            fields: [
                { type: 'label', text: 'を' },
                {
                    type: 'dropdown',
                    name: 'MODE',
                    rectColour: MODE_OPTIONS.rectColour,
                    options: [...MODE_OPTIONS.options],
                },
                { type: 'label', text: 'で表示する ' },
            ],
        },
    ],
    onFieldChange(block: Blockly.Block, fieldName: string, newValue: string) {
        if (fieldName !== 'MODE') return;
        const connection = block.getInput('VALUE')?.connection;
        if (!connection) return;

        suppressShadowSound();

        if (newValue === 'FLOAT') {
            connection.setShadowDom(buildShadow('float_seven_seg', { NUM: '0.0' }));
        } else {
            connection.setShadowDom(buildShadow('number_seven_seg', { NUM: '0' }));
        }

        const targetBlock = connection.targetBlock();
        if (targetBlock?.isShadow()) {
            Blockly.Events.disable();
            try {
                targetBlock.dispose(false);
                (connection as any).respawnShadow_();
            } finally {
                Blockly.Events.enable();
            }
        }
    },
});
